import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    
    // Listen for global cart count updates
    this.#setupGlobalCartCountListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Handles cart update events
   * @param {CustomEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    // Update cart count in the header when cart is updated
    this.#updateCartCount(event.detail.data?.itemCount);
  };

  /**
   * Updates the cart count display
   * @param {number} itemCount - The number of items in the cart
   */
  #updateCartCount(itemCount) {
    if (typeof itemCount !== 'number') return;
    
    // Update cart bubble count
    const cartBubble = this.querySelector('[ref="cartBubble"]');
    const cartBubbleCount = this.querySelector('[ref="cartBubbleCount"]');
    const cartBubbleText = this.querySelector('[ref="cartBubbleText"]');
    
    if (cartBubbleCount) {
      cartBubbleCount.textContent = itemCount.toString();
    }
    
    // Update accessibility text
    if (cartBubbleText) {
      const hiddenText = cartBubbleText.querySelector('.visually-hidden');
      if (hiddenText) {
        hiddenText.textContent = `Cart count: ${itemCount}`;
      }
    }
    
    // Show/hide cart bubble based on count
    if (cartBubble) {
      if (itemCount > 0) {
        cartBubble.classList.remove('visually-hidden');
        cartBubble.setAttribute('data-cart-count', itemCount.toString());
      } else {
        cartBubble.classList.add('visually-hidden');
        cartBubble.setAttribute('data-cart-count', '0');
      }
    }
    
    // Update cart button aria-label
    const cartButton = this.querySelector('[ref="cartButton"]');
    if (cartButton) {
      cartButton.setAttribute('aria-label', `Open cart drawer Cart count: ${itemCount}`);
    }
  }

  open() {
    this.showDialog();
    
    // Refresh cart data when drawer is opened
    this.#refreshCartData();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  #refreshCartData() {
    // Get the cart items component
    const cartItemsComponent = this.querySelector('cart-items-component');
    if (!cartItemsComponent) return;
    
    const sectionId = cartItemsComponent.getAttribute('data-section-id');
    if (!sectionId) return;
    
    // Refresh the cart section to get latest data
    sectionRenderer.renderSection(sectionId, { cache: false });
  }

  /**
   * Sets up listener for global cart count updates
   */
  #setupGlobalCartCountListener() {
    // For now, we'll rely on the CartAddEvent and CartUpdateEvent
    // The global functions will be handled by the existing event system
    // This method is kept for future enhancements
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
